import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  LinearProgress,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useContext, useState } from 'react';
import { ICoreUser } from '../../../../shared-interfaces/User/ICoreUser';
import { Inclusion } from '../../../../shared-types/Inclusion';
import { GetRoleHelperPlainText } from '../Functions/GetRoleHelperPlainText';
import { GetUserRoles } from '../Functions/GetUserRoles';
import { IUserRole } from '../Interfaces/IUserRole';
import UserManagementService from '../Services/UserManagementService';
import { UserManagementActionType } from '../Types/UserManagementActionType';
import { UserManagementDispatch, UserManagementState } from '../UserManagement';
import RoleHelperText from './RoleHelperText';
import SelectedUserList from './SelectedUserList';

interface IChangeRoleDialogProps {
  open: boolean;
  close: () => void;
  user?: ICoreUser;
  entryMethod: 'BulkOps' | 'UserList';
}

interface IChangeRoleDialogContentProps {
  users: ICoreUser[] | undefined;
  role: IUserRole;
  onRoleChange: (event: any) => void;
  onUserClear?: (user: ICoreUser) => void;
  entryMethod: 'BulkOps' | 'UserList';
}

export const ChangeRoleDialogContent = (
  props: IChangeRoleDialogContentProps,
) => {
  const roles = GetUserRoles();
  return (
    <>
      <div style={{ margin: '10px 0 0 0' }}>
        <InputLabel id="role-select-label">
          For the selected users, change the role to
        </InputLabel>
        <Select
          labelId="role-select-label"
          id="role-select"
          value={props.role.value}
          onChange={e => props.onRoleChange(e)}
          style={{ width: '200px' }}
          renderValue={() => props.role.name}
        >
          {roles.map((x, i) => (
            //@ts-ignore
            <MenuItem key={i} value={x.value} component="div">
              <ListItem dense={true} alignItems="flex-start">
                <ListItemText
                  primary={x.name}
                  secondary={<RoleHelperText role={x.value} />}
                  primaryTypographyProps={{
                    'aria-label': x.name,
                  }}
                  secondaryTypographyProps={{
                    'aria-label': GetRoleHelperPlainText(x.value),
                  }}
                />
              </ListItem>
            </MenuItem>
          ))}
        </Select>
      </div>
      <div style={{ margin: '35px 0 0 0' }}>
        <InputLabel>Users Impacted</InputLabel>
        <Paper
          variant="outlined"
          style={{
            maxHeight: '200px',
            overflowY: 'scroll',
            overflowX: 'hidden',
            margin: '10px 0 0 0',
          }}
        >
          <SelectedUserList
            actionType="role"
            users={props.users}
            onClear={props.onUserClear}
          />
        </Paper>
      </div>
    </>
  );
};

const ChangeRoleDialog = (props: IChangeRoleDialogProps) => {
  const state = useContext(UserManagementState);
  const dispatch = useContext(UserManagementDispatch);
  const { enqueueSnackbar } = useSnackbar();

  const roles = GetUserRoles();
  const [role, setRole] = useState<IUserRole>(roles[0]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleRoleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setRole(roles.find(x => x.value === event.target.value) as IUserRole);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    let updatedUsersWithExclude: ICoreUser[];

    let submittedUsers = props.user
      ? [props.user]
      : state.selectAll
      ? state.excludeUsers
      : state.selectedUsers;

    // Determine which set of users should be updated in the UI
    if (props.entryMethod === 'BulkOps' && state.selectAll) {
      updatedUsersWithExclude = state.users.filter(
        x => state.excludeUsers.findIndex(y => y.id === x.id) === -1,
      );
      updatedUsersWithExclude.forEach(x => (x.role = role.value));
    } else submittedUsers.forEach(x => (x.role = role.value));

    UserManagementService.bulkUpdateUserRole(
      role.value,
      submittedUsers.map(x => x.id),
      props.entryMethod === 'BulkOps' && state.selectAll
        ? Inclusion.Exclude
        : Inclusion.Include,
      state.filter,
    )
      .then(() => {
        dispatch({
          type: UserManagementActionType.UpdateUsers,
          value:
            props.entryMethod === 'BulkOps' && state.selectAll
              ? updatedUsersWithExclude
              : submittedUsers,
        });

        enqueueSnackbar('Successfully Changed Roles', {
          variant: 'success',
        });
        props.close();
      })
      .catch(e => {
        enqueueSnackbar('Failed to Change Roles', {
          variant: 'error',
        });
      })
      .finally(() => setIsSubmitting(false));
  };

  const handleUserClear = (user: ICoreUser) => {
    dispatch({
      type: UserManagementActionType.SetSelectedUsers,
      value: state.selectedUsers.filter(u => u.id !== user.id),
    });
  };

  return (
    <>
      <Dialog
        open={props.open}
        onClose={props.close}
        fullWidth={true}
        maxWidth="sm"
      >
        <DialogTitle>Change Role</DialogTitle>
        {props.user || state.selectedUsers.length > 0 || state.selectAll ? (
          <>
            <DialogContent>
              <ChangeRoleDialogContent
                users={props.user ? [props.user] : undefined}
                role={role}
                onRoleChange={e => handleRoleChange(e)}
                onUserClear={u => handleUserClear(u)}
                entryMethod={props.entryMethod}
              />
            </DialogContent>
            <DialogActions>
              <Button
                color="secondary"
                variant="text"
                onClick={() => props.close()}
              >
                Cancel
              </Button>
              <Button
                disabled={isSubmitting}
                color="primary"
                onClick={handleSubmit}
              >
                Confirm
              </Button>
            </DialogActions>
            {isSubmitting && <LinearProgress />}
          </>
        ) : (
          <>
            <DialogContent>
              <Box
                height="200px"
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <Typography variant="h2">
                  Select user(s) before changing roles.
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                color="secondary"
                variant="text"
                onClick={() => props.close()}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default ChangeRoleDialog;
